import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe,
  Check,
  ChevronUp,
  X,
  Send,
  RotateCcw,
  Phone,
  MessageCircle,
  Calendar,
  Sparkles,
  MapPin
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { KNOWLEDGE_BASE, matchUserIntent } from './chatbot/chatKnowledge';

const FloatingActions = () => {
  const { language, setLanguage, availableLanguages } = useLanguage();
  const navigate = useNavigate();

  // Chatbot state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [isChatTooltipVisible, setIsChatTooltipVisible] = useState(false);

  // Language Popover state
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const langMenuRef = useRef(null);

  // Close language menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    if (isLangMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangMenuOpen]);

  // Sync welcome greeting when language changes or on mount
  useEffect(() => {
    const welcomeData = KNOWLEDGE_BASE.welcome[language] || KNOWLEDGE_BASE.welcome.mr;
    if (messages.length === 0 || (messages.length === 1 && messages[0].id.startsWith('welcome-'))) {
      setMessages([
        {
          id: `welcome-${language}`,
          sender: 'bot',
          text: `${welcomeData.title}\n\n${welcomeData.message}`,
          quickActions: welcomeData.quickActions,
          timestamp: new Date()
        }
      ]);
    }
  }, [language, messages.length]);

  // Scroll to bottom when messages update
  useEffect(() => {
    if (isChatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, isChatOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (isChatOpen) {
      setHasUnread(false);
      setIsLangMenuOpen(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isChatOpen]);

  // Toast auto-dismiss
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 2200);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const handleSelectLanguage = (code) => {
    if (code === language) {
      setIsLangMenuOpen(false);
      return;
    }
    setLanguage(code);
    setIsLangMenuOpen(false);

    let toastText = '';
    if (code === 'mr') {
      toastText = '✓ भाषा बदलली: मराठी';
    } else if (code === 'kn') {
      toastText = '✓ ಭಾಷೆ ಬದಲಾಗಿದೆ: ಕನ್ನಡ';
    } else {
      toastText = '✓ Language changed to English';
    }
    setToastMessage(toastText);
  };

  const handleOpenChat = () => {
    setIsChatOpen(true);
    setHasUnread(false);
    setIsLangMenuOpen(false);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  const handleRestartChat = () => {
    const welcomeData = KNOWLEDGE_BASE.welcome[language] || KNOWLEDGE_BASE.welcome.mr;
    setMessages([
      {
        id: `welcome-${Date.now()}`,
        sender: 'bot',
        text: `${welcomeData.title}\n\n${welcomeData.message}`,
        quickActions: welcomeData.quickActions,
        timestamp: new Date()
      }
    ]);
  };

  const handleQuickAction = (topicId) => {
    const topic = KNOWLEDGE_BASE.topics[topicId];
    if (!topic) return;

    const topicData = topic[language] || topic.en || topic.mr;
    const welcomeData = KNOWLEDGE_BASE.welcome[language] || KNOWLEDGE_BASE.welcome.mr;
    const actionObj = welcomeData.quickActions?.find((a) => a.id === topicId);
    const userLabel = actionObj ? actionObj.label : topicId;

    const userMsg = {
      id: `usr-${Date.now()}`,
      sender: 'user',
      text: userLabel,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          text: topicData.text,
          action: topicData.action,
          timestamp: new Date()
        }
      ]);
    }, 550);
  };

  const handleSendMessage = (e) => {
    e?.preventDefault();
    const query = inputText.trim();
    if (!query) return;

    const userMsg = {
      id: `usr-${Date.now()}`,
      sender: 'user',
      text: query,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputText('');
    setIsTyping(true);

    setTimeout(() => {
      const intent = matchUserIntent(query, language);
      let replyText = '';
      let actionObj = null;

      if (intent && KNOWLEDGE_BASE.topics[intent]) {
        const data = KNOWLEDGE_BASE.topics[intent][language] || KNOWLEDGE_BASE.topics[intent].mr;
        replyText = data.text;
        actionObj = data.action;
      } else {
        if (language === 'mr') {
          replyText = `धन्यवाद! आपल्या प्रश्नावर अधिक माहितीसाठी आमचे नेत्रतज्ज्ञ उपलब्ध आहेत.\n\nतुम्ही खालील पर्यायांमधून निवडू शकता किंवा थेट आमच्या २४/७ हेल्पलाइनवर संपर्क करू शकता:\n📞 **70835 92101**`;
        } else if (language === 'kn') {
          replyText = `ಧನ್ಯವಾದಗಳು! ನಿಮ್ಮ ಪ್ರಶ್ನೆಗೆ ಹೆಚ್ಚಿನ ಮಾಹಿತಿಗಾಗಿ ನಮ್ಮ ನೇತ್ರ ತಜ್ಞರು ಲಭ್ಯವಿದ್ದಾರೆ.\n\nಕೆಳಗಿನ ಆಯ್ಕೆಗಳಿಂದ ಆಯ್ಕೆ ಮಾಡಿ ಅಥವಾ ನೇರವಾಗಿ ನಮ್ಮ ಸಹಾಯವಾಣಿಗೆ ಕರೆ ಮಾಡಿ:\n📞 **70835 92101**`;
        } else {
          replyText = `Thank you! Our eye care team is available for comprehensive guidance on your query.\n\nYou can choose from the clinical topics below or call our 24/7 helpline directly:\n📞 **+91 70835 92101**`;
        }
        actionObj = { label: "📅 Book Consultation", type: "appointment" };
      }

      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          text: replyText,
          action: actionObj,
          quickActions: intent ? null : (KNOWLEDGE_BASE.welcome[language] || KNOWLEDGE_BASE.welcome.mr).quickActions,
          timestamp: new Date()
        }
      ]);
    }, 650);
  };

  const handleActionClick = (action) => {
    if (!action) return;
    if (action.type === 'appointment') {
      setIsChatOpen(false);
      navigate('/appointment');
    } else if (action.type === 'navigate' && action.url) {
      setIsChatOpen(false);
      navigate(action.url);
    } else if (action.type === 'tel' && action.number) {
      window.location.href = `tel:${action.number}`;
    }
  };

  const renderFormattedText = (text) => {
    return text.split('\n').map((line, i) => {
      if (!line.trim()) {
        return <div key={i} className="h-2" />;
      }
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <p key={i} className="leading-relaxed">
          {parts.map((part, pIdx) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return (
                <strong key={pIdx} className="font-semibold text-slate-900">
                  {part.slice(2, -2)}
                </strong>
              );
            }
            return <span key={pIdx}>{part}</span>;
          })}
        </p>
      );
    });
  };

  const currentLangObj = availableLanguages.find((l) => l.code === language) || availableLanguages[0];

  const placeholders = {
    mr: 'तुमचा प्रश्न येथे विचारा... (उदा. मोतीबिंदू, शाखा, वेळ)',
    kn: 'ನಿಮ್ಮ ಪ್ರಶ್ನೆ ಕೇಳಿ... (ಉದಾ. ಮೋತಿಬಿಂದು, ಶಾಖೆ, ಸಮಯ)',
    en: 'Type your question... (e.g. Cataract, LASIK, Doctors)'
  };

  return (
    <>
      {/* 
        ============================================================
        CREATIVE VERTICAL FLOATING ACTION HUB:
        - TOP (var): Chatbot Launcher Button (Robot Icon + Red Dot)
        - BOTTOM (khali): Language Button (Pill with Globe & Active Lang)
        ============================================================
      */}
      <aside
        aria-label="Floating Actions"
        className="fixed bottom-20 right-3.5 sm:bottom-6 sm:right-6 z-50 flex flex-col items-center sm:items-end gap-2.5 select-none print:hidden"
      >
        {/* Toast Notification */}
        <AnimatePresence>
          {toastMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.9 }}
              className="px-3.5 py-1.5 rounded-full bg-navy/95 backdrop-blur-md text-white text-xs font-medium shadow-xl border border-white/10 flex items-center gap-1.5 whitespace-nowrap pointer-events-none"
            >
              <Check size={13} className="text-teal" />
              <span>{toastMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Both buttons hide smoothly when Chat modal is open */}
        <AnimatePresence>
          {!isChatOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 15 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
              className="flex flex-col items-center sm:items-end gap-2.5"
            >
              {/* 
                --------------------------------------------------------
                1. VAR (TOP): CHATBOT LAUNCHER BUTTON (HIGHLIGHTED)
                - Radar pulse expanding waves
                - Ambient glowing halo
                - Floating AI Help callout badge with pulsing dot
                - Bobbing float animation
                - Circular 45° gradient (#7C3AED to #2563EB to #38BDF8)
                - Robot face icon
                - Animated ping red notification badge
                --------------------------------------------------------
              */}
              <div className="relative group">
                {/* 1. Radar Pulse Waves (Highlights effect) */}
                <motion.span
                  animate={{ scale: [1, 1.45, 1.75], opacity: [0.75, 0.35, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                  className="absolute inset-0 rounded-full bg-gradient-to-r from-[#7C3AED] via-[#2563EB] to-[#38BDF8] blur-xs pointer-events-none"
                />
                <motion.span
                  animate={{ scale: [1, 1.35, 1.6], opacity: [0.65, 0.25, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut', delay: 0.9 }}
                  className="absolute inset-0 rounded-full bg-gradient-to-r from-[#38BDF8] via-[#2563EB] to-[#7C3AED] blur-xs pointer-events-none"
                />

                {/* 2. Ambient Glowing Halo */}
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#7C3AED] via-[#38BDF8] to-[#2563EB] opacity-75 blur-md animate-pulse pointer-events-none" />

                {/* Desktop Hover Tooltip */}
                <div
                  className={`hidden sm:flex absolute right-full mr-3 top-1/2 -translate-y-1/2 items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900/95 text-white text-xs font-medium shadow-xl border border-white/10 whitespace-nowrap transition-all duration-200 pointer-events-none z-20 ${
                    isChatTooltipVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
                  }`}
                >
                  <Sparkles size={13} className="text-[#38BDF8]" />
                  <span>
                    {language === 'mr'
                      ? 'AI नेत्र सहाय्यक • २४/७'
                      : language === 'kn'
                      ? 'AI ನೇತ್ರ ಸಹಾಯಕ • ೨೪/೭'
                      : 'AI Eye Assistant • 24/7'}
                  </span>
                </div>

                <motion.button
                  onClick={handleOpenChat}
                  onMouseEnter={() => setIsChatTooltipVisible(true)}
                  onMouseLeave={() => setIsChatTooltipVisible(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.92 }}
                  animate={{ y: [0, -4, 0] }}
                  transition={{
                    y: { duration: 2.8, repeat: Infinity, ease: 'easeInOut' }
                  }}
                  className="relative z-10 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center cursor-pointer shadow-[0_10px_25px_-3px_rgba(37,99,235,0.55),0_4px_16px_rgba(124,58,237,0.45)] hover:shadow-[0_14px_35px_-3px_rgba(37,99,235,0.75),0_8px_20px_rgba(124,58,237,0.55)] focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-400 transition-shadow duration-300"
                  style={{
                    background: 'linear-gradient(45deg, #7C3AED 0%, #2563EB 52%, #38BDF8 100%)'
                  }}
                  title={
                    language === 'mr'
                      ? 'AI नेत्र सहाय्यक उघडा'
                      : language === 'kn'
                      ? 'AI ನೇತ್ರ ಸಹಾಯಕ ತೆರೆಯಿರಿ'
                      : 'Open Sree Tekke AI Assistant'
                  }
                  aria-label="Open AI Chatbot"
                >
                  {/* Centered White Line-Art Robot Icon */}
                  <svg
                    width="29"
                    height="29"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="transform -translate-y-0.5 drop-shadow-md"
                  >
                    <path d="M12 8V4H8" />
                    <rect width="16" height="12" x="4" y="8" rx="2.5" />
                    <path d="M2 14h2" />
                    <path d="M20 14h2" />
                    <line x1="9" x2="9" y1="12.5" y2="15.5" />
                    <line x1="15" x2="15" y1="12.5" y2="15.5" />
                  </svg>

                  {/* Top-Right Red Notification Badge Dot with Ping */}
                  {hasUnread && (
                    <span className="absolute top-0 right-0 flex h-3.5 w-3.5 sm:h-4 sm:w-4 pointer-events-none">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-80" />
                      <span className="relative inline-flex rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 bg-[#EF4444] border-2 border-[#0A1424] shadow-xs" />
                    </span>
                  )}
                </motion.button>
              </div>

              {/* 
                --------------------------------------------------------
                2. KHALI (BOTTOM): CREATIVE LANGUAGE BUTTON & POPUP
                - Sleek rounded-full pill matching user's image
                - Globe with animated teal pulse dot
                - Active Language Name (English / मराठी / ಕನ್ನಡ)
                - Micro-chevron indicator
                - Click reveals interactive language popover
                --------------------------------------------------------
              */}
              <div ref={langMenuRef} className="relative">
                {/* Upward Language Popover Menu */}
                <AnimatePresence>
                  {isLangMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.92 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.92 }}
                      transition={{ type: 'spring', stiffness: 450, damping: 30 }}
                      className="absolute bottom-full right-0 mb-2.5 p-1.5 bg-white/98 backdrop-blur-xl rounded-2xl shadow-[0_16px_40px_rgba(10,20,36,0.22),0_0_0_1px_rgba(0,0,0,0.06)] border border-slate-100 min-w-[175px] z-50 flex flex-col gap-1"
                    >
                      <div className="px-3 py-1.5 flex items-center justify-between border-b border-slate-100 text-[10px] uppercase font-bold tracking-wider text-slate-400">
                        <span>भाषा / Language</span>
                        <Globe size={11} className="text-clinical" />
                      </div>

                      {availableLanguages.map((lang) => {
                        const isActive = language === lang.code;
                        return (
                          <button
                            key={lang.code}
                            onClick={() => handleSelectLanguage(lang.code)}
                            className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 text-left ${
                              isActive
                                ? 'bg-clinical text-white shadow-sm'
                                : 'text-slate-700 hover:text-navy hover:bg-slate-100/80'
                            }`}
                          >
                            <div className="flex flex-col leading-tight">
                              <span>{lang.native}</span>
                              <span
                                className={`text-[10px] ${
                                  isActive ? 'text-white/80' : 'text-slate-400'
                                }`}
                              >
                                {lang.label}
                              </span>
                            </div>
                            {isActive && <Check size={14} className="text-white shrink-0 ml-2" />}
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* The Language Switcher Pill */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsLangMenuOpen((prev) => !prev)}
                  className={`relative flex items-center gap-2 px-3.5 py-1.5 sm:py-2 rounded-full bg-white/95 backdrop-blur-md border shadow-[0_8px_20px_-4px_rgba(10,20,36,0.16)] transition-all duration-200 text-slate-800 ${
                    isLangMenuOpen
                      ? 'border-clinical ring-2 ring-clinical/20 shadow-md'
                      : 'border-slate-200/90 hover:border-clinical/40 hover:shadow-lg'
                  }`}
                  title="Change language / भाषा बदला"
                  aria-label="Change Language"
                  aria-expanded={isLangMenuOpen}
                >
                  {/* Globe with teal pulse dot */}
                  <div className="relative flex items-center justify-center">
                    <Globe size={16} className="text-clinical" />
                    <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-teal" />
                    </span>
                  </div>

                  {/* Active Language Label */}
                  <span className="text-xs font-bold text-navy tracking-wide">
                    {currentLangObj.native}
                  </span>

                  {/* Micro-chevron */}
                  <ChevronUp
                    size={13}
                    className={`text-slate-400 transition-transform duration-200 ${
                      isLangMenuOpen ? 'rotate-180 text-clinical' : ''
                    }`}
                  />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </aside>

      {/* 
        ============================================================
        3. CHATBOT MODAL WINDOW (Interactive Conversation)
        ============================================================
      */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.94 }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="fixed bottom-20 right-3.5 left-3.5 sm:left-auto sm:right-6 sm:bottom-6 w-auto sm:w-[410px] h-[590px] max-h-[calc(100vh-140px)] bg-white rounded-2xl shadow-[0_20px_50px_rgba(10,20,36,0.35),0_0_0_1px_rgba(0,0,0,0.06)] flex flex-col overflow-hidden select-text z-50"
            role="dialog"
            aria-modal="true"
            aria-label="Sree Tekke AI Chatbot"
          >
            {/* Header */}
            <div className="relative px-4 py-3.5 bg-gradient-to-r from-navy via-slate-900 to-clinical text-white flex flex-col gap-2 shrink-0 shadow-md">
              <div className="flex items-center justify-between">
                {/* Robot Avatar and Title */}
                <div className="flex items-center gap-2.5">
                  <div className="relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-md bg-gradient-to-tr from-[#7C3AED] via-[#2563EB] to-[#38BDF8]">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#FFFFFF"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="transform -translate-y-0.5"
                    >
                      <path d="M12 8V4H8" />
                      <rect width="16" height="12" x="4" y="8" rx="2.5" />
                      <path d="M2 14h2" />
                      <path d="M20 14h2" />
                      <line x1="9" x2="9" y1="12.5" y2="15.5" />
                      <line x1="15" x2="15" y1="12.5" y2="15.5" />
                    </svg>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-navy" />
                  </div>

                  <div>
                    <h3 className="text-sm font-bold leading-snug tracking-wide flex items-center gap-1.5">
                      <span>
                        {language === 'mr'
                          ? 'श्री टेके AI नेत्र सहाय्यक'
                          : language === 'kn'
                          ? 'ಶ್ರೀ ಟೆಕ್ಕೆ AI ನೇತ್ರ ಸಹಾಯಕ'
                          : 'Sree Tekke AI Assistant'}
                      </span>
                    </h3>
                    <p className="text-[11px] text-teal/90 font-medium flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span>
                        {language === 'mr'
                          ? '२४/७ ऑनलाइन क्लिनिकल मदत'
                          : language === 'kn'
                          ? '೨೪/೭ ಆನ್‌ಲೈನ್ ಕ್ಲಿನಿಕಲ್ ನೆರವು'
                          : '24/7 Online Clinical Help'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 text-white/80">
                  <button
                    onClick={handleRestartChat}
                    className="p-1.5 rounded-full hover:bg-white/10 hover:text-white transition-colors"
                    title={
                      language === 'mr'
                        ? 'संभाषण पुन्हा सुरू करा'
                        : language === 'kn'
                        ? 'ಸಂಭಾಷಣೆ ಮರುಹೊಂದಿಸಿ'
                        : 'Restart chat'
                    }
                    aria-label="Restart conversation"
                  >
                    <RotateCcw size={15} />
                  </button>
                  <button
                    onClick={handleCloseChat}
                    className="p-1.5 rounded-full hover:bg-white/10 hover:text-white transition-colors"
                    title="Close"
                    aria-label="Close chat"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* In-chat Language Switcher Bar */}
              <div className="flex items-center justify-between pt-1 border-t border-white/10 text-xs">
                <span className="text-[11px] text-white/70">
                  {language === 'mr' ? 'भाषा निवडा:' : language === 'kn' ? 'ಭಾಷೆ ಆಯ್ಕೆ:' : 'Language:'}
                </span>
                <div className="flex items-center gap-1 bg-white/10 p-0.5 rounded-full">
                  {[
                    { code: 'mr', label: 'मराठी' },
                    { code: 'kn', label: 'ಕನ್ನಡ' },
                    { code: 'en', label: 'English' }
                  ].map((l) => (
                    <button
                      key={l.code}
                      onClick={() => setLanguage(l.code)}
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-all ${
                        language === l.code
                          ? 'bg-clinical text-white shadow-sm'
                          : 'text-white/80 hover:text-white'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Emergency OPD Hotline Banner */}
            <div className="bg-amber-50 px-3.5 py-1.5 border-b border-amber-200/60 flex items-center justify-between text-xs text-amber-900 shrink-0">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-800">
                <Phone size={12} className="text-amber-600" />
                <span>
                  {language === 'mr'
                    ? 'तातडीची मदत: 7083592101'
                    : language === 'kn'
                    ? 'ತುರ್ತು ಸಹಾಯವಾಣಿ: 7083592101'
                    : 'Emergency OPD: 7083592101'}
                </span>
              </span>
              <a
                href="tel:+917083592101"
                className="text-[10px] uppercase font-bold text-clinical hover:underline"
              >
                {language === 'mr' ? 'कॉल करा' : language === 'kn' ? 'ಕರೆ ಮಾಡಿ' : 'Call'}
              </a>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 p-3.5 overflow-y-auto space-y-3.5 bg-gradient-to-b from-slate-50 to-white text-xs">
              {messages.map((msg) => {
                const isBot = msg.sender === 'bot';

                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isBot ? 'items-start' : 'items-end'}`}
                  >
                    <div
                      className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-xs shadow-sm ${
                        isBot
                          ? 'bg-white border border-slate-200/90 text-slate-800 rounded-tl-sm'
                          : 'bg-clinical text-white rounded-tr-sm shadow-md'
                      }`}
                    >
                      {/* Message Content */}
                      <div className="space-y-1">{renderFormattedText(msg.text)}</div>

                      {/* Bot Action CTA Button */}
                      {msg.action && (
                        <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center">
                          <button
                            onClick={() => handleActionClick(msg.action)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-clinical text-white font-semibold text-xs shadow-sm hover:bg-navy transition-all duration-200"
                          >
                            {msg.action.type === 'appointment' ? (
                              <Calendar size={13} />
                            ) : msg.action.type === 'navigate' ? (
                              <MapPin size={13} />
                            ) : (
                              <Phone size={13} />
                            )}
                            <span>{msg.action.label}</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Quick Suggestion Chips */}
                    {msg.quickActions && msg.quickActions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 max-w-[95%]">
                        {msg.quickActions.map((qa) => (
                          <button
                            key={qa.id}
                            onClick={() => handleQuickAction(qa.id)}
                            className="px-2.5 py-1 rounded-full bg-white border border-slate-200/90 text-[11px] font-medium text-slate-700 hover:text-clinical hover:border-clinical/40 hover:bg-blue-50/50 shadow-xs transition-all active:scale-95"
                          >
                            {qa.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Typing Animation */}
              {isTyping && (
                <div className="flex items-center gap-1.5 p-2 bg-white border border-slate-200/80 rounded-2xl w-16 shadow-xs">
                  <span className="w-1.5 h-1.5 bg-clinical rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1.5 h-1.5 bg-clinical rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1.5 h-1.5 bg-clinical rounded-full animate-bounce" />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Form Bar */}
            <form
              onSubmit={handleSendMessage}
              className="p-2.5 bg-white border-t border-slate-200/90 flex items-center gap-2 shrink-0"
            >
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={placeholders[language] || placeholders.mr}
                className="flex-1 text-xs px-3.5 py-2.5 rounded-full border border-slate-300 focus:outline-none focus:border-clinical focus:ring-1 focus:ring-clinical text-slate-800 placeholder:text-slate-400 bg-slate-50/60"
              />

              {/* Direct WhatsApp Reception */}
              <a
                href="https://wa.me/917083592101?text=Hello%2C%20I%20would%20like%20to%20consult%20Sree%20Tekke%20Eye%20Clinic."
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full text-white bg-[#25D366] hover:bg-[#1ebd5b] transition-colors shrink-0 shadow-xs"
                title="WhatsApp Direct Reception"
                aria-label="WhatsApp"
              >
                <MessageCircle size={17} />
              </a>

              {/* Send Button */}
              <button
                type="submit"
                disabled={!inputText.trim()}
                className="p-2 rounded-full text-white bg-clinical hover:bg-navy disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0 shadow-xs"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingActions;
